import { router, useLocalSearchParams } from 'expo-router';
import { useCallback, useState } from 'react';

import { stripPhoneNumber } from '@fhs/ui/src/components/form-text-inputs/masks';
import {
  AuthOptInType,
  AuthV2ChallengeType,
  useSignUpV2Mutation,
} from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { useGetMeLazyQuery } from '@fhs-legacy/frontend/src/generated/rbi-graphql';

import { cognitoSignIn } from '../cognito';
import { SignUpError, SignUpInput } from '../types';

import { useAuthContext, useCRMEventsContext, useToast } from '.';

export const useSignUp = () => {
  const [signUpError, setSignUpError] = useState<SignUpError | null>(null);
  //When we are fully on auth V2 we should remove this state and just handle the authenticated state ourselves.
  const { refreshCurrentUserWithNewSession, originLocation } = useAuthContext();
  const { logSignUpAttempt, logLoginEvent } = useCRMEventsContext();
  const { show } = useToast();
  const { loginJwt } = useLocalSearchParams<{ loginJwt?: string }>();
  const [getMeQuery] = useGetMeLazyQuery({
    fetchPolicy: 'network-only',
  });
  const onSignUp = useCallback((data, _clientOptions) => {
    return data.signUpV2.loginJwt;
  }, []);
  const onError = useCallback(
    err => {
      if (err?.graphQLErrors && err.graphQLErrors.length > 0) {
        //Take the highest level error code
        const code = err.graphQLErrors[0].extensions.code;
        switch (code) {
          case 'TOO_MANY_REQUESTS':
            show({
              text: 'You have exceeded the limit of attempts. Please try again later.',
              variant: 'informative',
            });
            break;
          default:
            if (code in SignUpError) {
              setSignUpError(code);
            } else {
              show({ text: 'Oops, something went wrong. Please try again', variant: 'negative' });
            }
        }
      } else {
        show({ text: 'Oops, something went wrong. Please try again', variant: 'negative' });
      }
    },
    [show]
  );
  const [internalSignUp] = useSignUpV2Mutation({ onCompleted: onSignUp, onError });

  /**
   * Polls the GetMe query until a loyaltyId is returned
   * This is needed because loyalty user creation happens asynchronously after sign-up
   */
  const pollForLoyaltyUserCreation = useCallback(async (): Promise<void> => {
    const maxRetries = 30; // Maximum number of retries (30 * 2 seconds = 1 minute)
    const retryDelay = 2000; // 2 seconds between retries
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        const { data } = await getMeQuery({
          //Dummy variables to satisfy the query
          variables: {
            numUniquePurchasedItems: 0,
            customInput: { shouldUniqueByModifiers: false },
          },
        });

        // Check if the result contains a loyaltyId
        if (data?.me?.loyaltyId) {
          return; // Success - loyalty user has been created
        }

        // If no loyaltyId yet, wait and retry
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      } catch (error) {
      } finally {
        retryCount++;
        if (retryCount < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, retryDelay));
        }
      }
    }

    // If we've exhausted all retries, the user can still proceed
    // The loyalty user might be created later
  }, [getMeQuery]);

  const signUp = useCallback(
    async ({ name, email, phoneNumber, dob, wantsPromotionalEmail }: SignUpInput) => {
      const dateOfBirth =
        dob && dob.dobMonth !== '' && dob.dobDay !== '' && dob.dobYear !== ''
          ? `${dob.dobYear}-${dob.dobMonth}-${dob.dobDay}`
          : undefined;
      try {
        if (loginJwt) {
          //Push event to Amplitude
          dob && logSignUpAttempt({ dob });
          const response = await internalSignUp({
            variables: {
              input: {
                name,
                loginJwt,
                email,
                phoneNumber: stripPhoneNumber(phoneNumber),
                dob: dateOfBirth,
                optIns: [
                  {
                    identifier: 'EMAIL_MARKETING' as unknown as AuthOptInType,
                    optInStatus: wantsPromotionalEmail ?? false,
                  },
                ],
              },
            },
          });
          const signUpResult = response.data?.signUpV2;
          if (signUpResult) {
            switch (signUpResult.challengeType) {
              case AuthV2ChallengeType.SIGN_IN_CHALLENGE: {
                const { challengeCode, cognitoId, sessionId } = signUpResult.challenge;
                //Authenticate with Cognito.
                const session = await cognitoSignIn(cognitoId, sessionId, challengeCode);

                //Validate the user is authenticated here
                if (session) {
                  //Poll until the user is created on loyalty.
                  await pollForLoyaltyUserCreation();
                  //Update Local storage session info and trigger downstream listeners
                  await refreshCurrentUserWithNewSession();
                  //Push event to Amplitude
                  logLoginEvent({ method: 'signUp' });

                  if (originLocation === 'PYPESTREAM' || originLocation === '/support') {
                    // authContext needs time to update the cognitoId, to avoid a race condition, give a small wait time before navigating
                    await new Promise(resolve => setTimeout(resolve, 300));
                    router.dismissTo({ pathname: '/support', params: { loginJwt: undefined } });
                  } else {
                    // Route to home page & clear login state
                    router.dismissTo({ pathname: '/', params: { loginJwt: undefined } });
                  }
                }
                break;
              }
              case AuthV2ChallengeType.SIGN_UP_CHALLENGE:
              case AuthV2ChallengeType.IDENTIFY_CHALLENGE: {
                throw Error("Something went wrong, these aren't valid states");
              }
            }
          }
        } else {
          throw Error('Invalid Sign Up attempt');
        }
      } catch (e) {
        show({
          text: 'Oops, something went wrong. Please try again',
          variant: 'informative',
          duration: 5000,
        });
      }
    },
    [
      loginJwt,
      logSignUpAttempt,
      internalSignUp,
      pollForLoyaltyUserCreation,
      refreshCurrentUserWithNewSession,
      logLoginEvent,
      show,
    ]
  );
  return { signUp, signUpError, setSignUpError };
};
